import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { InfiniteScrollContainer } from "@/shared/components/InfiniteScrollContainer";
import { cn } from "@/shared/lib/utils";
import { getSeriesGroupId, type ImagingSeries } from "@/portals/patient/api/imagingStudiesApi";

// Height of exactly 3 rows (each ~70px: 16px padding top+bottom + a ~38px
// two-line text block) plus the two 12px gaps between them — the container
// only becomes scrollable past this point, so up to 3 series show in full
// with no scrollbar, and a 4th introduces one instead of growing the card
// further down the page. The class name must stay a static string literal
// (not interpolated) so Tailwind's build-time scanner actually generates it.
const VISIBLE_ROWS_BEFORE_SCROLL = 3;
const MAX_HEIGHT_CLASS_FOR_3_ROWS = "max-h-[236px]";

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** Full, paginated history of the patient's uploaded studies, grouped by
 *  series (Phase 2 — see ImagingSeries) — one row per series_instance_uid,
 *  modality, upload date, and a frame/file-count badge, no thumbnails/
 *  previews per spec. Selecting a row opens the viewer for that series.
 *  Scrolls internally (via the shared InfiniteScrollContainer — the same
 *  scroll-near-bottom mechanic UploadHistoryList already uses) and fetches
 *  10 more from the backend once the patient scrolls near the end, rather
 *  than ever loading the whole history at once. */
export function ImagingStudyList({
  series,
  onSelect,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: {
  series: ImagingSeries[];
  onSelect: (series: ImagingSeries) => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}) {
  return (
    <InfiniteScrollContainer
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      onLoadMore={onLoadMore}
      className={cn("pr-1", series.length > VISIBLE_ROWS_BEFORE_SCROLL && MAX_HEIGHT_CLASS_FOR_3_ROWS)}
    >
      <div className="grid gap-3">
        {series.map((s) => (
          <Button
            // NOT s.seriesInstanceUid — two distinct groups can share that
            // raw tag (see getSeriesGroupId's own docstring); a plain
            // seriesInstanceUid key here would collide and make React
            // misrender/reuse rows across genuinely different groups.
            key={getSeriesGroupId(s)}
            variant="outline"
            onClick={() => onSelect(s)}
            className="!flex !h-auto !w-full !min-w-0 !items-center !justify-start !gap-3 !rounded-2xl !border !border-[#E5E7EB] !bg-white !p-4 !text-left hover:!bg-[#FFF8F8] hover:!shadow-md"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#E4002B]/50">
              <Icon icon="lucide:scan-line" className="h-4 w-4 !text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                <p className="truncate text-sm font-extrabold text-[#0A2947]">
                  {s.title || s.files[0]?.originalFilename || "Untitled study"}
                </p>
                {s.modality && (
                  <span className="shrink-0 rounded-full bg-[#F3F4F6] px-2 py-0.5 text-[10px] font-bold uppercase text-[#6B7280]">
                    {s.modality}
                  </span>
                )}
                {/* One badge, not two — a multi-FILE series shows its file
                    count (each member is its own image), a single multi-
                    FRAME file shows its frame count instead; showing both
                    at once for a plain single-file/single-frame study would
                    just repeat "1" twice for no reason. */}
                {s.fileCount > 1 ? (
                  <span className="shrink-0 rounded-full bg-[#EFF6FF] px-2 py-0.5 text-[10px] font-bold text-[#1D4ED8]">
                    {s.fileCount} files
                  </span>
                ) : s.frameCount > 1 ? (
                  <span className="shrink-0 rounded-full bg-[#EFF6FF] px-2 py-0.5 text-[10px] font-bold text-[#1D4ED8]">
                    {s.frameCount} frames
                  </span>
                ) : null}
              </div>
              <p className="mt-0.5 text-xs text-[#9CA3AF]">Uploaded {formatDate(s.createdAt)}</p>
            </div>
            <Icon icon="lucide:chevron-right" className="h-4 w-4 shrink-0 text-[#9CA3AF]" />
          </Button>
        ))}
      </div>
    </InfiniteScrollContainer>
  );
}
